import { CartAddEvent } from '@theme/events';

const SWATCH = {
  black: '#161616',
  'midnight navy': '#1d2742',
  navy: '#1d2742',
  wine: '#5b1d2c',
  sage: '#6f8a6a',
  'charcoal green': '#2f3d35',
  green: '#2f5a3f',
};

const rupees = (paise) => '₹' + Math.round(paise / 100).toLocaleString('en-IN');
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Section ids of every cart-items-component, so the add response re-renders the drawer/cart in one go. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function addToCart(items, sourceId) {
  const sections = cartSectionIds();
  const res = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
  });
  const json = await res.json();
  if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');
  document.dispatchEvent(
    new CartAddEvent({}, sourceId, {
      source: 'begun-offer',
      itemCount: items.reduce((n, i) => n + i.quantity, 0),
      sections: json.sections,
    })
  );
  return json;
}

/* ---------- Movement Bundle customiser ---------- */

class MovementBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    const raw = JSON.parse(this.querySelector('[data-mb-data]').textContent);
    this.items = raw.map((p) => {
      const isBra = p.kind === 'bra';
      const style = titleCase(p.type.replace(/\s*(SPORTS BRA|LEGGINGS?)\s*$/i, ''));
      const [prefix, color = ''] = p.title.split(isBra ? / Sports Bra /i : / Leggings /i);
      const descriptor = prefix.slice(style.length).trim();
      return { ...p, style, descriptor, color: color.trim() || 'Default' };
    });

    this.state = { bra: {}, bottom: {} };
    for (const kind of ['bra', 'bottom']) {
      const def = this.items.find((p) => p.handle === this.dataset[kind === 'bra' ? 'defaultBra' : 'defaultBottom']);
      const first = def || this.items.find((p) => p.kind === kind && p.variants.some((v) => v.available));
      if (first) this.state[kind] = { style: first.style, handle: first.handle, size: null };
      this.renderKind(kind);
    }
    this.renderSummary();

    this.form = this.querySelector('[data-mb-form]');
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    this.querySelector('[data-mb-sticky-atc]')?.addEventListener('click', () => this.submit());
    this.setupSticky();
  }

  product(kind) {
    return this.items.find((p) => p.handle === this.state[kind].handle);
  }

  styles(kind) {
    const seen = new Map();
    for (const p of this.items.filter((p) => p.kind === kind)) {
      if (!seen.has(p.style)) seen.set(p.style, { style: p.style, descriptor: p.descriptor, available: false });
      if (p.variants.some((v) => v.available)) seen.get(p.style).available = true;
    }
    return [...seen.values()];
  }

  /** Builds a native radio input + label; native radios give arrow-key navigation and SR semantics for free. */
  radio({ name, value, label, checked, disabled, cls, extra = '', describe = '' }) {
    const id = `${name}-${value}`.replace(/[^a-z0-9-]/gi, '-');
    return `<span class="${cls}">
      <input type="radio" id="${id}" name="${name}" value="${value}" ${checked ? 'checked' : ''} ${disabled ? 'aria-disabled="true"' : ''} class="mb__radio">
      <label for="${id}">${extra}<span class="mb__opt-text">${label}</span>${describe}</label>
    </span>`;
  }

  renderKind(kind) {
    const st = this.state[kind];
    const current = this.product(kind);
    const uid = this.closest('section')?.getAttribute('aria-labelledby') || 'mb';

    // Styles
    const stylesEl = this.querySelector(`[data-mb-styles="${kind}"]`);
    stylesEl.innerHTML = this.styles(kind)
      .map((s) =>
        this.radio({
          name: `${uid}-${kind}-style`,
          value: s.style,
          label: s.style,
          checked: s.style === st.style,
          disabled: !s.available,
          cls: 'mb__style',
          describe: `<span class="mb__opt-desc">${s.descriptor}${s.available ? '' : ' · Sold out'}</span>`,
        })
      )
      .join('');
    stylesEl.onchange = (e) => {
      if (this.rejectDisabled(e, kind)) return;
      const style = e.target.value;
      const sameColour = this.items.find((p) => p.kind === kind && p.style === style && p.color === current?.color);
      const next =
        (sameColour && sameColour.variants.some((v) => v.available) && sameColour) ||
        this.items.find((p) => p.kind === kind && p.style === style && p.variants.some((v) => v.available)) ||
        this.items.find((p) => p.kind === kind && p.style === style);
      this.select(kind, next);
    };

    // Colours for the chosen style
    const colours = this.items.filter((p) => p.kind === kind && p.style === st.style);
    const coloursEl = this.querySelector(`[data-mb-colors="${kind}"]`);
    coloursEl.innerHTML = colours
      .map((p) => {
        const soldOut = !p.variants.some((v) => v.available);
        const hex = SWATCH[p.color.toLowerCase()] || '#999';
        return this.radio({
          name: `${uid}-${kind}-color`,
          value: p.handle,
          label: `<span class="visually-hidden">${p.color}${soldOut ? ', sold out' : ''}</span>`,
          checked: p.handle === st.handle,
          disabled: soldOut,
          cls: 'mb__swatch' + (soldOut ? ' is-soldout' : ''),
          extra: `<span class="mb__chip" style="--chip:${hex}" aria-hidden="true"></span>`,
        });
      })
      .join('');
    coloursEl.onchange = (e) => !this.rejectDisabled(e, kind) && this.select(kind, this.items.find((p) => p.handle === e.target.value));
    this.querySelector(`[data-mb-colorname="${kind}"]`).textContent = current?.color || '';

    // Sizes for the chosen product
    const sizesEl = this.querySelector(`[data-mb-sizes="${kind}"]`);
    sizesEl.innerHTML = (current?.variants || [])
      .map((v) =>
        this.radio({
          name: `${uid}-${kind}-size`,
          value: v.size,
          label: v.size + (v.available ? '' : '<span class="visually-hidden">, sold out</span>'),
          checked: v.size === st.size,
          disabled: !v.available,
          cls: 'mb__size' + (v.available ? '' : ' is-soldout'),
        })
      )
      .join('');
    sizesEl.onchange = (e) => {
      if (this.rejectDisabled(e, kind)) return;
      st.size = e.target.value;
      this.querySelector(`[data-mb-error="${kind}"]`).hidden = true;
      this.querySelector(`[data-mb-sizename="${kind}"]`).textContent = st.size;
      this.renderSummary();
    };
    this.querySelector(`[data-mb-sizename="${kind}"]`).textContent = st.size || 'Select';

    // Block selecting aria-disabled (sold out) radios while keeping them focusable/announced.
    for (const input of this.querySelectorAll(`[data-mb-styles="${kind}"] input, [data-mb-colors="${kind}"] input, [data-mb-sizes="${kind}"] input`)) {
      input.addEventListener('click', (e) => {
        if (input.getAttribute('aria-disabled') === 'true') e.preventDefault();
      });
    }

    // Live preview tile
    const img = this.querySelector(`[data-mb-img="${kind}"]`);
    if (current) {
      img.src = current.image;
      img.alt = current.title;
      this.querySelector(`[data-mb-cap="${kind}"]`).textContent = `${current.style} · ${current.color}`;
    }
  }

  /** Arrow keys can still check a sold-out (aria-disabled) radio; undo it and say why. */
  rejectDisabled(e, kind) {
    if (e.target.getAttribute('aria-disabled') !== 'true') return false;
    this.renderKind(kind);
    this.announce('That option is sold out');
    return true;
  }

  select(kind, product) {
    if (!product) return;
    const st = this.state[kind];
    const prevSize = st.size;
    st.style = product.style;
    st.handle = product.handle;
    // Keep the shopper's size when the new pick has it in stock; otherwise clear it and say why.
    const keep = product.variants.find((v) => v.size === prevSize && v.available);
    st.size = keep ? prevSize : null;
    this.renderKind(kind);
    this.renderSummary();
    if (prevSize && !keep) this.announce(`Size ${prevSize} is sold out in ${product.style} ${product.color}. Please choose another size.`);
  }

  renderSummary() {
    const bra = this.product('bra');
    const bottom = this.product('bottom');
    if (!bra || !bottom) return;
    const total = bra.price + bottom.price;
    const price = Number(this.dataset.price);
    const was = this.querySelector('[data-mb-was]');
    const save = this.querySelector('[data-mb-save]');
    if (total > price) {
      was.innerHTML = `<span class="visually-hidden">Regular price </span>${rupees(total)}`;
      save.textContent = `Save ${rupees(total - price)}`;
    } else {
      was.textContent = '';
      save.textContent = '';
    }
    const line = (kind, p) =>
      `<li><span>${p.style} ${kind === 'bra' ? 'bra' : 'leggings'} · ${p.color}</span><span>${this.state[kind].size || '<em>pick size</em>'}</span></li>`;
    this.querySelector('[data-mb-summary]').innerHTML =
      `<p class="mb__summary-title">Your bundle</p><ul>${line('bra', bra)}${line('bottom', bottom)}<li><span>Mystery gift</span><span>Free</span></li></ul>`;
  }

  announce(msg) {
    const el = this.querySelector('[data-mb-status]');
    el.textContent = '';
    requestAnimationFrame(() => (el.textContent = msg));
  }

  async submit() {
    if (this.busy) return;
    const missing = ['bra', 'bottom'].filter((k) => !this.state[k].size);
    for (const k of ['bra', 'bottom']) this.querySelector(`[data-mb-error="${k}"]`).hidden = !missing.includes(k);
    if (missing.length) {
      const target = this.querySelector(`[data-mb-sizes="${missing[0]}"] input:not([aria-disabled="true"])`);
      target?.focus({ preventScroll: true });
      target?.closest('.mb__group')?.scrollIntoView({ block: 'center' });
      return;
    }

    const bra = this.product('bra');
    const bottom = this.product('bottom');
    const bv = bra.variants.find((v) => v.size === this.state.bra.size);
    const lv = bottom.variants.find((v) => v.size === this.state.bottom.size);
    const properties = {
      'Sports bra': `${bra.style} ${bra.descriptor} – ${bra.color}`.replace(/\s+/g, ' '),
      'Bra size': bv.size,
      Leggings: `${bottom.style} ${bottom.descriptor} – ${bottom.color}`.replace(/\s+/g, ' '),
      'Leggings size': lv.size,
      _bra_variant_id: String(bv.id),
      _bra_sku: bv.sku || '',
      _leggings_variant_id: String(lv.id),
      _leggings_sku: lv.sku || '',
    };

    const buttons = this.querySelectorAll('[data-mb-atc], [data-mb-sticky-atc]');
    const label = this.querySelector('[data-mb-atc-label]');
    const original = label.textContent;
    this.busy = true;
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    label.textContent = 'Adding…';
    try {
      await addToCart([{ id: Number(this.dataset.variantId), quantity: 1, properties }], this.id || 'movement-bundle');
      label.textContent = 'Added ✓';
      this.announce('Bundle added to cart');
    } catch (err) {
      label.textContent = original;
      this.announce(err.message);
      alert(err.message);
    } finally {
      this.busy = false;
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
      setTimeout(() => (label.textContent = original), 2000);
    }
  }

  setupSticky() {
    const bar = this.querySelector('[data-mb-sticky]');
    const atc = this.querySelector('[data-mb-atc]');
    if (!bar || !atc || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      bar.hidden = entry.isIntersecting;
    }).observe(atc);
  }
}

if (!customElements.get('movement-bundle')) customElements.define('movement-bundle', MovementBundle);

/* ---------- Cart "add one more" quick-add ---------- */

if (!window.__begunOfferNudge) {
  window.__begunOfferNudge = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element && e.target.closest('[data-bo-add]');
    if (!btn) return;
    e.preventDefault();
    const card = btn.closest('[data-bo-item]');
    const select = card?.querySelector('select');
    const id = Number(select ? select.value : btn.dataset.variantId);
    if (!id) {
      select?.focus();
      return;
    }
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      await addToCart([{ id, quantity: 1 }], 'bundle-nudge');
    } catch (err) {
      btn.textContent = label;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      const msg = card?.querySelector('[data-bo-msg]');
      if (msg) msg.textContent = err.message;
    }
  });
}
